
import React, { useState, useEffect } from 'react';
import { AbandonedCart } from '../types';
import { api } from '../services/api';
import { useNotifications } from './NotificationContext';
import { useSettings } from './SettingsContext';

const AbandonedCarts: React.FC = () => {
  const { settings, updateSettings } = useSettings();
  const { addNotification } = useNotifications();
  const [carts, setCarts] = useState<AbandonedCart[]>([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);

  useEffect(() => {
    const fetchCarts = async () => {
      setLoading(true);
      try {
        const data = await api.abandonment.getAll();
        if (data && Array.isArray(data)) {
          setCarts(data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCarts();
  }, []);

  const handleManualRecovery = async (id: string) => {
    setIsProcessing(id);
    try {
      await api.abandonment.recoverCart(id);
      setCarts(prev => prev.map(c => c.id === id ? { ...c, status: 'Recovering', recoveryEmailSent: true } : c));
      addNotification({ 
        title: 'Recovery Started', 
        message: 'Follow-up sent to the customer.', 
        type: 'success' 
      });
    } catch (e) {
      addNotification({ title: 'Recovery Failed', message: 'Unable to trigger sequence.', type: 'error' });
    } finally {
      setIsProcessing(null);
    }
  };

  const trappedRevenue = carts
    .filter(c => c.status === 'Captured' || c.status === 'Recovering')
    .reduce((sum, c) => sum + c.planPrice, 0);

  const recoveryRate = carts.length > 0 
    ? ((carts.filter(c => c.status === 'Recovered').length / carts.length) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Abandoned Carts</h1>
          <p className="text-slate-500 dark:text-slate-400 text-lg">Identify and recover "Ghost Prospects" before they churn forever.</p>
        </div>
        <div className="flex bg-white dark:bg-slate-800 p-2 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm items-center gap-4">
           <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Automation</span>
           <button 
            onClick={() => updateSettings({ checkoutConfig: { ...settings.checkoutConfig, recoveryConfig: { ...settings.checkoutConfig.recoveryConfig, automatedSequence: !settings.checkoutConfig.recoveryConfig.automatedSequence } } })}
            className={`w-14 h-7 rounded-full relative p-1 transition-all ${settings.checkoutConfig.recoveryConfig.automatedSequence ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-700'}`}
           >
              <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform ${settings.checkoutConfig.recoveryConfig.automatedSequence ? 'translate-x-7' : 'translate-x-0'}`}></div>
           </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-slate-800 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-700 shadow-sm relative overflow-hidden group">
           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Trapped Revenue</p>
           <p className="text-4xl font-black text-slate-900 dark:text-white">${trappedRevenue.toLocaleString()}</p>
           <p className="text-xs text-emerald-500 font-bold mt-2 tracking-tight">Potentially recoverable in next 24h</p>
           <div className="absolute -right-8 -bottom-8 opacity-5 group-hover:opacity-10 transition-opacity">
              <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.56-7.43H5.05"/><path d="m15 5-2 2 2 2"/><path d="m9 9 2-2-2-2"/></svg>
           </div>
        </div>
        <div className="bg-white dark:bg-slate-800 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-700 shadow-sm relative overflow-hidden group">
           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Recovery Efficiency</p>
           <p className="text-4xl font-black text-indigo-600 dark:text-indigo-400">{recoveryRate}%</p>
           <p className="text-xs text-slate-400 font-bold mt-2 tracking-tight">Benchmark for SaaS: 12-18%</p>
        </div>
        <div className="bg-slate-900 dark:bg-black p-8 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden">
           <h4 className="text-sm font-black uppercase tracking-widest mb-2 text-indigo-400">Recovery Logic</h4>
           <div className="space-y-3 mt-6">
              <div className="flex justify-between items-center text-xs">
                 <span className="text-slate-400 font-bold">1st Sequence:</span>
                 <span className="font-black text-white">{settings.checkoutConfig.recoveryConfig.delayHours} Hour(s) after</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                 <span className="text-slate-400 font-bold">Method:</span>
                 <span className="font-black text-white">Dynamic Magic Link</span>
              </div>
           </div>
           <div className="absolute top-0 right-0 p-8 opacity-10">
              <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>
           </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-[3rem] border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-24 text-center">
             <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
             <p className="text-slate-400 font-black uppercase text-[10px] tracking-widest">Hydrating Abandonment Ledger...</p>
          </div>
        ) : carts.length === 0 ? (
          <div className="p-24 text-center bg-slate-50/50 dark:bg-slate-900/20">
             <div className="w-20 h-20 bg-white dark:bg-slate-800 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm border border-slate-100 dark:border-slate-700">
                <svg className="text-slate-200 dark:text-slate-700" xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.56-7.43H5.05"/><path d="m15 5-2 2 2 2"/><path d="m9 9 2-2-2-2"/></svg>
             </div>
             <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Zero Leaks Detected</h3>
             <p className="text-slate-400 text-sm mt-2 max-w-sm mx-auto">No recent checkouts have been abandoned. Your funnel is currently airtight.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
             <table className="w-full text-left">
                <thead>
                   <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700">
                      <th className="px-10 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Prospect Identity</th>
                      <th className="px-10 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Intended Value</th>
                      <th className="px-10 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Session Age</th>
                      <th className="px-10 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Protocol Status</th>
                      <th className="px-10 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                   </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                   {carts.map(cart => (
                     <tr key={cart.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors">
                        <td className="px-10 py-6">
                           <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-black">
                                 {cart.email.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                 <p className="text-sm font-black text-slate-900 dark:text-white">{cart.email}</p>
                                 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">{cart.planTier} Plan</p>
                              </div>
                           </div>
                        </td>
                        <td className="px-10 py-6">
                           <span className="text-sm font-black text-slate-900 dark:text-white">${cart.planPrice}</span>
                        </td>
                        <td className="px-10 py-6">
                           <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{new Date(cart.abandonedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </td>
                        <td className="px-10 py-6 text-center">
                           <span className={`inline-block px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                             cart.status === 'Recovered' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                             cart.status === 'Recovering' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                             'bg-slate-50 text-slate-400 border-slate-100'
                           }`}>{cart.status}</span>
                        </td>
                        <td className="px-10 py-6 text-right">
                           {cart.status === 'Recovered' ? (
                             <div className="flex items-center justify-end gap-2 text-emerald-500 font-black text-[10px] uppercase">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                Conversion Secured
                             </div>
                           ) : (
                             <button 
                              onClick={() => handleManualRecovery(cart.id)}
                              disabled={isProcessing === cart.id || cart.status === 'Recovering'}
                              className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${
                                cart.status === 'Recovering' ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95'
                              }`}
                             >
                               {isProcessing === cart.id ? 'Firing...' : cart.status === 'Recovering' ? 'In Progress' : 'Recover Cart'}
                             </button>
                           )}
                        </td>
                     </tr>
                   ))}
                </tbody>
             </table>
          </div>
        )}
      </div>
      
      {/* Design Tip */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 p-8 rounded-[2.5rem] border border-indigo-100 dark:border-indigo-800 flex gap-6 items-center">
         <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-2xl flex items-center justify-center text-xl shadow-inner">💡</div>
         <p className="text-xs text-indigo-700 dark:text-indigo-300 font-medium leading-relaxed">
            <b>Pro Tip:</b> Use the <span className="underline cursor-pointer hover:text-indigo-900" onClick={() => (window as any).setActiveView('email_studio')}>Email Designer</span> to create high-urgency recovery templates with a <b>20% discount coupon code</b> to maximize recovery probability.
         </p>
      </div>
    </div>
  );
};

export default AbandonedCarts;
